import {
    AppTable,
    PageHeader,
    LoaderBar,
    FlexBox,
    FlexColumn,
    FlexRowBetween,
} from 'components'
import { Form, Card, Button } from 'semantic-ui-react'
import { useEffect, useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
    FormDateField,
    FormTextFormField,
    FormDropdownField,
    SubmitCancelButtons,
} from 'elements'
import {
    AuthService,
    DatePickerTypes,
    DateUtility,
    DateFormat,
    InvoiceService,
} from 'utility'
import { GetInvoiceSearch } from 'hooks/invoice'
import { GetUserList, GetCustomerList, useNotification } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { DesktopMode, MobileMode } from 'layout'
import styled from 'styled-components'
import InvoiceEmailModal from './invoiceEmailModal'

const CompletedScreenContainer = styled.div`
    .error {
        color: ${({ theme }) => theme.colors.danger};
    }
`

const GroupCardMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;

    .label {
        font-weight: 600;
        color: #334155;
        min-width: 100px;
        flex-shrink: 0;
    }

    .value {
        color: #0f172a;
        font-weight: 500;
    }
`

const CompletedInvoiceSearchSchema = yup.object().shape({
    FromInvoiceDate: yup
        .string()
        .required('From date required')
        .typeError('From date required'),
    ToInvoiceDate: yup
        .string()
        .required('To date required')
        .typeError('To date required'),
    CustomerId: yup.array().min(0).nullable(),
    AssignedUserId: yup.array().min(0).nullable(),
    InvoiceNumber: yup.string().nullable(),
    CustomerNameOrEmail: yup.string().nullable(),
})

const CompletedInvoiceCard = ({ item, handleView, handleOpenSend }) => (
    <Card fluid className="mb-3">
        <Card.Content>
            <Card.Header style={{ fontWeight: 600, color: '#0f172a' }}>
                Invoice #{item.InvoiceNumber}
            </Card.Header>
            <Card.Description className="mt-2">
                <GroupCardMetaRow>
                    <span className="label">Customer</span>
                    <span className="value">{item.CustomerName}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Email</span>
                    <span className="value">{item.CustomerEmail}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Invoice Date</span>
                    <span className="value">
                        {item.InvoiceDate &&
                            DateUtility.dateToLocalISO(
                                item.InvoiceDate,
                                DateFormat.date,
                            )}
                    </span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Due Date</span>
                    <span className="value">
                        {item.DueDate &&
                            DateUtility.dateToLocalISO(
                                item.DueDate,
                                DateFormat.date,
                            )}
                    </span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Total Amount</span>
                    <span className="value fw-semibold">
                        {item.TotalAmount} {item.CurrencyCode}
                    </span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Outstanding</span>
                    <span className="value">{item.AmountOutstanding}</span>
                </GroupCardMetaRow>
                <FlexBox className="justify-content-end gap-2 mt-3">
                    <Button size="small" onClick={() => handleView(item.InvoiceId)}>
                        View
                    </Button>
                    <Button
                        size="small"
                        color="green"
                        onClick={() => handleOpenSend(item)}
                    >
                        Send
                    </Button>
                </FlexBox>
            </Card.Description>
        </Card.Content>
    </Card>
)

export const CompletedInvoiceSearchScreen = () => {
    const { data, loading, filterChanged, pagination, pageChanged, setData } =
        GetInvoiceSearch()

    const companyId = AuthService.getUser()?.Companies?.[0]?.CompanyId
    const navigate = useNavigate()
    const { successToast, errorToast } = useNotification()
    const { options: userOptions } = GetUserList()
    const { options: customerOptions } = GetCustomerList()

    const {
        control,
        handleSubmit,
        reset,
        watch,
        getValues,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(CompletedInvoiceSearchSchema),
        defaultValues: {
            FromInvoiceDate: new Date(),
            ToInvoiceDate: new Date(),
            CustomerId: [],
            AssignedUserId: [],
            InvoiceNumber: '',
            CustomerNameOrEmail: '',
        },
    })

    const FromInvoiceDate = watch('FromInvoiceDate')

    const submit = formData => {
        const payload = {
            CompanyId: companyId,
            FromInvoiceDate: DateUtility.dateToLocalISO(
                DateUtility.startDay(new Date(formData.FromInvoiceDate)),
            ),
            ToInvoiceDate: DateUtility.dateToLocalISO(
                DateUtility.endDay(new Date(formData.ToInvoiceDate)),
            ),
            CustomerId:
                Array.isArray(formData.CustomerId) && formData.CustomerId.length
                    ? formData.CustomerId.toString().replaceAll(',', ';')
                    : '',
            AssignedUserId:
                Array.isArray(formData.AssignedUserId) &&
                    formData.AssignedUserId.length
                    ? formData.AssignedUserId.toString().replaceAll(',', ';')
                    : '',
            Status: 5, // Completed
            InvoiceNumber: formData.InvoiceNumber || '',
            CustomerNameOrEmail: formData.CustomerNameOrEmail || '',
            PageNumber: 1,
            PageSize: 50,
        }
        filterChanged(payload)
    }

    // initial load
    useEffect(() => {
        const defaults = getValues()
        submit(defaults)
    }, [])

    const resetForm = async () => {
        reset({
            FromInvoiceDate: new Date(),
            ToInvoiceDate: new Date(),
            CustomerId: [],
            AssignedUserId: [],
            InvoiceNumber: '',
            CustomerNameOrEmail: '',
        })
        setData([])
    }

    // Email modal state
    const [openEmailModal, setOpenEmailModal] = useState(false)
    const [sendingEmail, setSendingEmail] = useState(false)
    const [selectedInvoice, setSelectedInvoice] = useState(null)

    const handleView = invoiceId => {
        navigate(`/app/invoice/view/${invoiceId}`)
    }

    const handleOpenSend = invoice => {
        setSelectedInvoice(invoice)
        setOpenEmailModal(true)
    }

    const handleSubmitInvoiceEmail = async toEmail => {
        if (!selectedInvoice?.InvoiceId) return
        try {
            setSendingEmail(true)
            const companyId =
                selectedInvoice?.CompanyId ??
                AuthService.getUser()?.Companies?.[0]?.CompanyId
            const invNo =
                selectedInvoice?.InvoiceNumberDisplay ||
                selectedInvoice?.InvoiceNumber ||
                ''
            const payload = {
                InvoiceId: selectedInvoice.InvoiceId,
                CompanyId: companyId,
                ToEmail: toEmail,
                Cc: '',
                Subject: invNo
                    ? `Invoice ${invNo} from eAuditor`
                    : 'Invoice from eAuditor',
                Body: 'Dear Customer,\n\nPlease find your attached invoice.\n\nThank you.',
                AttachPdf: true,
                PdfFilePath: '',
            }
            await InvoiceService.sendEmail(payload)
            successToast('Invoice email sent')
            setOpenEmailModal(false)
            setSelectedInvoice(null)
        } catch (e) {
            console.error('Failed to send invoice email', e)
            errorToast('Failed to send invoice email')
        } finally {
            setSendingEmail(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                title: 'Invoice #',
                key: 'InvoiceNumber',
            },
            {
                title: 'Invoice Date',
                render: item => (
                    <span>
                        {item.InvoiceDate &&
                            DateUtility.dateToLocalISO(
                                item.InvoiceDate,
                                DateFormat.date,
                            )}
                    </span>
                ),
            },
            {
                title: 'Due Date',
                render: item => (
                    <span>
                        {item.DueDate &&
                            DateUtility.dateToLocalISO(
                                item.DueDate,
                                DateFormat.date,
                            )}
                    </span>
                ),
            },
            {
                title: 'Customer',
                key: 'CustomerName',
            },
            {
                title: 'Email',
                key: 'CustomerEmail',
            },
            {
                title: 'Total',
                key: 'TotalAmount',
            },
            {
                title: 'Outstanding',
                key: 'AmountOutstanding',
            },
            {
                title: 'Currency',
                key: 'CurrencyCode',
            },
            {
                title: 'Actions',
                textAlign: 'center',
                render: item => (
                    <FlexBox className="justify-content-center align-items-center gap-2 flex-wrap">
                        <Button onClick={() => handleView(item.InvoiceId)}>
                            View
                        </Button>
                        <Button
                            color="green"
                            onClick={() => handleOpenSend(item)}
                        >
                            Send
                        </Button>
                    </FlexBox>
                ),
            },
        ],
        [handleView, handleOpenSend],
    )

    return (
        <CompletedScreenContainer>
            {loading && <LoaderBar content="Loading Completed Invoices..." />}
            <DesktopMode>
                <FlexRowBetween className="mb-3">
                    <PageHeader header="Completed Invoices" />
                </FlexRowBetween>
            </DesktopMode>
            <MobileMode>
                <FlexColumn className="mb-3">
                    <PageHeader header="Completed Invoices" />
                </FlexColumn>
            </MobileMode>

            <Form noValidate>
                <Form.Group widths="equal">
                    <FormDateField
                        name="FromInvoiceDate"
                        control={control}
                        label="From Date"
                        errors={errors.FromInvoiceDate}
                        type={DatePickerTypes.Single}
                    />
                    <FormDateField
                        name="ToInvoiceDate"
                        control={control}
                        label="To Date"
                        disabledBefore={FromInvoiceDate}
                        errors={errors.ToInvoiceDate}
                        type={DatePickerTypes.Single}
                    />
                    <FormTextFormField
                        control={control}
                        name="InvoiceNumber"
                        label="Invoice #"
                        errors={errors.InvoiceNumber}
                        placeholder="e.g. 000001"
                    />
                </Form.Group>
                <Form.Group widths="equal">
                    <FormTextFormField
                        control={control}
                        name="CustomerNameOrEmail"
                        label="Customer (name/email)"
                        errors={errors.CustomerNameOrEmail}
                        placeholder="Search text"
                    />
                    <FormDropdownField
                        control={control}
                        name="AssignedUserId"
                        label="User"
                        errors={errors.AssignedUserId}
                        options={userOptions}
                        placeholder="Select User"
                        defaultValue={[]}
                        selection
                        clearable
                        multiple
                    />
                    <FormDropdownField
                        control={control}
                        name="CustomerId"
                        label="Customer"
                        errors={errors.CustomerId}
                        options={customerOptions}
                        placeholder="Select Customer"
                        defaultValue={[]}
                        selection
                        clearable
                        multiple
                    />
                </Form.Group>
                <div className="pt-1">
                    <SubmitCancelButtons
                        onSubmit={handleSubmit(submit)}
                        onCancel={resetForm}
                        className=""
                    />
                </div>
            </Form>

            <div className="my-3">
                <AppTable
                    columns={columns}
                    data={data || []}
                    filter={{
                        PageNumber: pagination.currentPage,
                        PageSize: pagination.perPage,
                    }}
                    pageChanged={pageChanged}
                    totalCount={pagination.total}
                    emptyMessage="No Completed Invoices Found"
                    mobileCard={
                        <CompletedInvoiceCard
                            handleView={handleView}
                            handleOpenSend={handleOpenSend}
                        />
                    }
                />
                <InvoiceEmailModal
                    open={openEmailModal}
                    onClose={() => setOpenEmailModal(false)}
                    onSubmit={handleSubmitInvoiceEmail}
                    loading={sendingEmail}
                />
            </div>
        </CompletedScreenContainer>
    )
}
